import { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { Pencil, Trash2, UserPlus } from 'lucide-react';

import { ROUTE_URLS } from '../../routes';
import { createLink } from '../../utils';
import { DataTable, DeleteConfirmModal, PageHeader, RowActions, SearchInput } from '../../components';
import { useDeleteUser, useBranches, useUsers } from '../../hooks';
import { useI18n } from '../../i18n';

export const Users = () => {
  const { t } = useI18n();
  const { mutateAsync: deleteUser } = useDeleteUser();
  const { data: branches, isLoading: isBranchesLoading } = useBranches();
  const { data: users, isLoading: isUsersLoading, isError, refetch } = useUsers();
  const [search, setSearch] = useState('');

  const findConnectedBranches = useCallback(
    user => {
      return (
        branches?.filter(branch => {
          return branch.users.find(conUser => {
            return conUser.id.toString() === user.id.toString();
          });
        }) ?? []
      );
    },
    [branches],
  );

  const columns = useMemo(
    () => [
      {
        title: t('users.name'),
        dataIndex: 'login',
        sorter: (a, b) => a.login.localeCompare(b.login),
        render: (text, user) => (
          <div className='wm-cell-main'>
            <span className='wm-avatar wm-avatar--sm'>{user.login.slice(0, 1).toUpperCase()}</span>
            <div>
              <Link to={createLink(ROUTE_URLS.EDIT_USER, user.id)} className='wm-link-strong'>
                {user.login}
              </Link>
              <div className='wm-muted' style={{ fontSize: 12 }}>
                {user.email || t('users.noEmail')}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: t('users.branches'),
        dataIndex: 'id',
        key: 'branches',
        render: (text, user) => {
          const connected = findConnectedBranches(user);
          if (!connected.length) return <span className='wm-muted'>{t('users.noBranches')}</span>;
          return (
            <div className='wm-chips'>
              {connected.map(branch => (
                <Link
                  key={branch.id}
                  to={createLink(ROUTE_URLS.EDIT_BRANCH, branch.id)}
                  className='wm-chip wm-chip--link'
                >
                  {branch.name}
                </Link>
              ))}
            </div>
          );
        },
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        width: 64,
        align: 'center',
        render: (text, user) => (
          <RowActions
            items={[
              { key: 'edit', icon: Pencil, label: t('common.edit'), to: createLink(ROUTE_URLS.EDIT_USER, user.id) },
              { type: 'divider', key: 'd' },
              {
                key: 'delete',
                icon: Trash2,
                danger: true,
                label: t('common.delete'),
                onClick: () => {
                  setSelectedUserId(user.id);
                  setShowDeleteModal(true);
                },
              },
            ]}
          />
        ),
      },
    ],
    [findConnectedBranches, t],
  );

  const dataSource = useMemo(() => {
    const list = Array.isArray(users) ? users.filter(user => !user.isSU) : [];
    const query = search.trim().toLowerCase();
    return query
      ? list.filter(
          user =>
            user.login.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query),
        )
      : list;
  }, [users, search]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const confirmHandler = () => {
    deleteUser(selectedUserId);
    resetState();
  };

  const resetState = () => {
    setShowDeleteModal(false);
    setSelectedUserId(null);
  };

  return (
    <>
      <DeleteConfirmModal open={showDeleteModal} onConfirm={confirmHandler} onCancel={resetState} />
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        actions={
          <Link to={ROUTE_URLS.CREATE_USER}>
            <Button type='primary' icon={<UserPlus size={16} />}>
              {t('users.add')}
            </Button>
          </Link>
        }
      />
      <div className='wm-toolbar'>
        <SearchInput value={search} onChange={setSearch} placeholder={t('users.searchPlaceholder')} />
        <span className='wm-toolbar__grow' />
        {Array.isArray(users) && (
          <span className='wm-badge wm-badge--plain'>
            {t('users.count', { count: users.filter(user => !user.isSU).length })}
          </span>
        )}
      </div>
      <DataTable
        dataSource={dataSource}
        columns={columns}
        isLoading={isBranchesLoading || isUsersLoading}
        isError={isError}
        onRetry={refetch}
        emptyVariant={search ? 'search' : undefined}
      />
    </>
  );
};
